import React, { useCallback, useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
} from '@mui/material';
import { listPageDialogPaperProps } from '../theme/darkDialog';
import {
  ListPageRoot,
  ListPageToolbar,
  ListPageTableSurface,
  ListPageTableHeadRow,
  ListPageTh,
  ListPageMobileStack,
  ListPageMobilePlaceholder,
  listPageTableBodyClass,
  listPageTableRowClass,
} from './listPage';
import { apiService } from '../services/api';
import {
  DxfLayerRule,
  DxfLayerRuleLineInput,
  CostLibraryItem,
  CostLibraryFilter,
} from '../types/api';
import { useAuth } from '../contexts/AuthContext';
import { hasPermission, permissions } from '../constants/permissions';
import { useDeleteConfirmation } from '../hooks/useDeleteConfirmation';

const MEASURE_KINDS: { value: number; label: string }[] = [
  { value: 1, label: 'Block insert count' },
  { value: 2, label: 'Polyline count' },
  { value: 3, label: 'Line length sum' },
  { value: 4, label: 'Polyline solid volume (area × thickness)' },
];

const BASE_UNITS: { value: number; label: string }[] = [
  { value: 1, label: 'Per piece' },
  { value: 2, label: 'Per meter' },
  { value: 3, label: 'Per cubic meter' },
];

interface DxfLayerRulesTabProps {
  onError: (msg: string) => void;
  onSuccess: (msg: string) => void;
  onRegisterCreateAction?: (handler: (() => void) | null) => void;
}

const emptyLine = (): DxfLayerRuleLineInput => ({
  costItemId: 0,
  quantityPerBase: 1,
  baseUnit: 1,
  sortOrder: 0,
});

const DxfLayerRulesTab: React.FC<DxfLayerRulesTabProps> = ({
  onError,
  onSuccess,
  onRegisterCreateAction,
}) => {
  const { user } = useAuth();
  const { confirmDelete, deleteConfirmationDialog } = useDeleteConfirmation();
  const canCreate = hasPermission(user, permissions.masterData.createDxfRules);
  const canEdit = hasPermission(user, permissions.masterData.editDxfRules);
  const canDelete = hasPermission(user, permissions.masterData.deleteDxfRules);
  const [rules, setRules] = useState<DxfLayerRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [dxfCode, setDxfCode] = useState('');
  const [measureKind, setMeasureKind] = useState(4);
  const [lines, setLines] = useState<DxfLayerRuleLineInput[]>([emptyLine()]);
  const [saving, setSaving] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerLineIndex, setPickerLineIndex] = useState(0);
  const [libraryItems, setLibraryItems] = useState<CostLibraryItem[]>([]);
  const [librarySearch, setLibrarySearch] = useState('');
  const [costItemLabelById, setCostItemLabelById] = useState<Record<number, string>>({});

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const data = await apiService.getDxfLayerRules();
      setRules(data);
    } catch (e) {
      console.error(e);
      onError('Failed to load DXF layer rules.');
    } finally {
      setLoading(false);
    }
  }, [onError]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    onRegisterCreateAction?.(canCreate ? () => openCreate() : null);
    return () => onRegisterCreateAction?.(null);
  }, [onRegisterCreateAction, canCreate]);

  useEffect(() => {
    if (!dialogOpen) return;
    const ids = Array.from(new Set(lines.map((line) => line.costItemId).filter((id) => id > 0))).filter(
      (id) => !costItemLabelById[id]
    );
    if (ids.length === 0) return;

    let cancelled = false;
    const hydrateLabels = async () => {
      const resolved = await Promise.all(
        ids.map(async (id) => {
          try {
            const item = await apiService.getCostItem(id);
            return [id, item.itemName] as const;
          } catch {
            return [id, `Item #${id}`] as const;
          }
        })
      );
      if (cancelled) return;
      setCostItemLabelById((prev) => {
        const next = { ...prev };
        for (const [id, label] of resolved) {
          next[id] = label;
        }
        return next;
      });
    };

    void hydrateLabels();
    return () => {
      cancelled = true;
    };
  }, [dialogOpen, lines, costItemLabelById]);

  const openCreate = () => {
    setEditingId(null);
    setDxfCode('');
    setMeasureKind(4);
    setLines([emptyLine()]);
    setCostItemLabelById({});
    setDialogOpen(true);
  };

  const openEdit = async (r: DxfLayerRule) => {
    try {
      const full = await apiService.getDxfLayerRule(r.dxfLayerRuleId);
      setEditingId(full.dxfLayerRuleId);
      setDxfCode(full.dxfCode);
      setMeasureKind(full.measureKind);
      setLines(
        full.lines.map((l) => ({
          costItemId: l.costItemId,
          quantityPerBase: l.quantityPerBase,
          baseUnit: l.baseUnit,
          sortOrder: l.sortOrder,
        }))
      );
      setDialogOpen(true);
    } catch (e) {
      console.error(e);
      onError('Failed to load rule.');
    }
  };

  const searchLibrary = async () => {
    try {
      const filter: CostLibraryFilter = { pageNumber: 1, pageSize: 80, searchTerm: librarySearch || undefined };
      const res = await apiService.getCostLibraryItems(filter);
      setLibraryItems(res.items);
    } catch (e) {
      console.error(e);
    }
  };

  const openPicker = (lineIndex: number) => {
    setPickerLineIndex(lineIndex);
    setPickerOpen(true);
    setLibrarySearch('');
    searchLibrary();
  };

  const pickCostItem = (item: CostLibraryItem) => {
    setLines((prev) => {
      const next = [...prev];
      next[pickerLineIndex] = { ...next[pickerLineIndex], costItemId: item.costItemId };
      return next;
    });
    setCostItemLabelById((prev) => ({ ...prev, [item.costItemId]: item.itemName }));
    setPickerOpen(false);
  };

  const save = async () => {
    if (!dxfCode.trim()) {
      onError('DxfCode is required.');
      return;
    }
    if (lines.some((l) => l.costItemId <= 0)) {
      onError('Each BOM line needs a valid cost item.');
      return;
    }
    const ids = lines.map((l) => l.costItemId);
    if (new Set(ids).size !== ids.length) {
      onError('Duplicate cost items are not allowed in one rule.');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        dxfCode: dxfCode.trim(),
        measureKind,
        lines: lines.map((l, i) => ({ ...l, sortOrder: i })),
      };
      if (editingId == null) {
        if (!canCreate) {
          onError('You do not have permission to create DXF rules.');
          return;
        }
        await apiService.createDxfLayerRule(payload);
      } else {
        if (!canEdit) {
          onError('You do not have permission to edit DXF rules.');
          return;
        }
        await apiService.updateDxfLayerRule(editingId, payload);
      }
      onSuccess(editingId == null ? 'Rule created.' : 'Rule updated.');
      setDialogOpen(false);
      await load();
    } catch (e: any) {
      console.error(e);
      onError(e?.response?.data?.message || e?.response?.data || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id: number) => {
    if (!canDelete) {
      onError('You do not have permission to delete DXF rules.');
      return;
    }
    const confirmed = await confirmDelete({ message: 'Delete this rule?' });
    if (!confirmed) return;
    try {
      await apiService.deleteDxfLayerRule(id);
      onSuccess('Rule deleted.');
      await load();
    } catch (e) {
      console.error(e);
      onError('Delete failed.');
    }
  };

  return (
    <ListPageRoot>
      <ListPageToolbar className="pb-3">
        <p className="text-sm text-slate-400 max-w-3xl">
          Map layers or block names to one or more cost items. The measure kind selects which DXF metric drives the BOM
          (volume, length, counts). Layers without a rule do not produce estimate lines on upload.
        </p>
      </ListPageToolbar>

      {loading ? (
        <div className="px-4 lg:px-5 pb-4">
          <ListPageMobilePlaceholder>Loading rules...</ListPageMobilePlaceholder>
        </div>
      ) : rules.length === 0 ? (
        <div className="px-4 lg:px-5 pb-4">
          <ListPageMobilePlaceholder>No rules yet.</ListPageMobilePlaceholder>
        </div>
      ) : (
        <div className="px-4 lg:px-5 pb-4">
          <ListPageTableSurface>
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead>
                  <ListPageTableHeadRow>
                    <ListPageTh>DXF code</ListPageTh>
                    <ListPageTh>Measure kind</ListPageTh>
                    <ListPageTh>BOM lines</ListPageTh>
                    <ListPageTh className="text-right">Actions</ListPageTh>
                  </ListPageTableHeadRow>
                </thead>
                <tbody className={listPageTableBodyClass}>
                  {rules.map((r) => (
                    <tr key={r.dxfLayerRuleId} className={listPageTableRowClass}>
                      <td className="px-5 py-4 font-medium text-white">{r.dxfCode}</td>
                      <td className="px-5 py-4 text-slate-300">
                        {MEASURE_KINDS.find((m) => m.value === r.measureKind)?.label ?? r.measureKind}
                      </td>
                      <td className="px-5 py-4 text-slate-400">{r.lines.length}</td>
                      <td className="px-5 py-4 text-right">
                        <div className="action-row-icon-buttons">
                          {canEdit && (
                            <button
                              type="button"
                              className="text-primary hover:bg-primary/10"
                              aria-label="Edit rule"
                              onClick={() => openEdit(r)}
                            >
                              <span className="material-symbols-outlined">edit</span>
                            </button>
                          )}
                          {canDelete && (
                            <button
                              type="button"
                              className="text-red-400 hover:bg-red-500/10"
                              aria-label="Delete rule"
                              onClick={() => remove(r.dxfLayerRuleId)}
                            >
                              <span className="material-symbols-outlined">delete</span>
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </ListPageTableSurface>

          <ListPageMobileStack>
            {rules.map((r) => (
              <div
                key={r.dxfLayerRuleId}
                className="bg-elevation-dark rounded-xl p-4 border border-border-dark shadow-lg flex flex-col gap-3"
              >
                <div className="flex items-center justify-between gap-2">
                  <p className="text-white font-semibold">{r.dxfCode}</p>
                  <span className="text-xs text-slate-400">{r.lines.length} lines</span>
                </div>
                <p className="text-sm text-slate-300">
                  {MEASURE_KINDS.find((m) => m.value === r.measureKind)?.label ?? r.measureKind}
                </p>
                <div className="action-row-icon-buttons border-t border-border-dark/50 pt-3">
                  {canEdit && (
                    <button type="button" className="text-primary hover:bg-primary/10" onClick={() => openEdit(r)}>
                      <span className="material-symbols-outlined">edit</span>
                    </button>
                  )}
                  {canDelete && (
                    <button
                      type="button"
                      className="text-red-400 hover:bg-red-500/10"
                      onClick={() => remove(r.dxfLayerRuleId)}
                    >
                      <span className="material-symbols-outlined">delete</span>
                    </button>
                  )}
                </div>
              </div>
            ))}
          </ListPageMobileStack>
        </div>
      )}

      <Dialog open={dialogOpen} onClose={() => !saving && setDialogOpen(false)} maxWidth="md" fullWidth PaperProps={listPageDialogPaperProps}>
        <DialogTitle>{editingId == null ? 'New DXF rule' : 'Edit DXF rule'}</DialogTitle>
        <DialogContent className="flex flex-col gap-4 pt-2">
          <TextField
            label="DXF code (layer or block name)"
            value={dxfCode}
            onChange={(e) => setDxfCode(e.target.value)}
            fullWidth
            size="small"
            margin="normal"
          />
          <TextField
            select
            label="Measure kind"
            value={measureKind}
            onChange={(e) => setMeasureKind(Number(e.target.value))}
            fullWidth
            size="small"
            margin="normal"
          >
            {MEASURE_KINDS.map((m) => (
              <MenuItem key={m.value} value={m.value}>
                {m.label}
              </MenuItem>
            ))}
          </TextField>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-slate-200">BOM lines</span>
              <button
                type="button"
                className="inline-flex items-center gap-1 rounded-lg bg-primary px-3 py-1.5 text-sm font-semibold text-white hover:bg-primary/90"
                onClick={() => setLines((prev) => [...prev, emptyLine()])}
              >
                <span className="material-symbols-outlined text-base">add</span>
                Add line
              </button>
            </div>
            {lines.map((line, idx) => (
              <div key={idx} className="grid grid-cols-1 sm:grid-cols-12 gap-2 items-end border border-border-dark rounded-lg p-3">
                <div className="sm:col-span-4">
                  <div className="text-xs text-slate-500 mb-1">Cost item</div>
                  <button
                    type="button"
                    className="w-full rounded border border-border-dark bg-elevation-dark px-2 py-2 text-left text-sm text-slate-200 hover:border-primary/50"
                    onClick={() => openPicker(idx)}
                  >
                    {line.costItemId > 0
                      ? (costItemLabelById[line.costItemId] ?? `Item #${line.costItemId}`)
                      : 'Pick from library…'}
                  </button>
                </div>
                <TextField
                  className="sm:col-span-3"
                  label="Qty per base"
                  type="number"
                  size="small"
                  value={line.quantityPerBase}
                  onChange={(e) => {
                    const v = parseFloat(e.target.value);
                    setLines((prev) => {
                      const n = [...prev];
                      n[idx] = { ...n[idx], quantityPerBase: isNaN(v) ? 0 : v };
                      return n;
                    });
                  }}
                />
                <TextField
                  className="sm:col-span-3"
                  select
                  label="Base unit"
                  size="small"
                  value={line.baseUnit}
                  onChange={(e) => {
                    const v = Number(e.target.value);
                    setLines((prev) => {
                      const n = [...prev];
                      n[idx] = { ...n[idx], baseUnit: v };
                      return n;
                    });
                  }}
                >
                  {BASE_UNITS.map((b) => (
                    <MenuItem key={b.value} value={b.value}>
                      {b.label}
                    </MenuItem>
                  ))}
                </TextField>
                <div className="sm:col-span-2 flex justify-end">
                  <button
                    type="button"
                    className="inline-flex size-9 items-center justify-center rounded-lg text-red-400 hover:bg-red-500/10"
                    aria-label={`Remove line ${idx + 1}`}
                    onClick={() => setLines((prev) => prev.filter((_, i) => i !== idx))}
                  >
                    <span className="material-symbols-outlined text-base">delete</span>
                  </button>
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
        <DialogActions>
          <button type="button" className="px-6 py-2 text-slate-400" onClick={() => setDialogOpen(false)} disabled={saving}>
            Cancel
          </button>
          <button
            type="button"
            className="rounded-lg bg-primary px-6 py-2 text-white font-semibold disabled:opacity-50"
            onClick={save}
            disabled={saving}
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </DialogActions>
      </Dialog>

      <Dialog open={pickerOpen} onClose={() => setPickerOpen(false)} maxWidth="sm" fullWidth PaperProps={listPageDialogPaperProps}>
        <DialogTitle>Pick cost item</DialogTitle>
        <DialogContent>
          <div className="flex gap-2 mb-3">
            <TextField
              size="small"
              fullWidth
              placeholder="Search…"
              value={librarySearch}
              onChange={(e) => setLibrarySearch(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && searchLibrary()}
            />
            <button type="button" className="rounded-lg bg-primary px-3 text-white text-sm" onClick={searchLibrary}>
              Search
            </button>
          </div>
          <div className="max-h-64 overflow-y-auto space-y-1">
            {libraryItems.map((it) => (
              <button
                key={it.costItemId}
                type="button"
                className="w-full text-left rounded border border-border-dark px-3 py-2 hover:bg-white/5 text-sm"
                onClick={() => pickCostItem(it)}
              >
                <span className="text-white font-medium">{it.itemCode}</span>
                <span className="text-slate-400"> — {it.itemName}</span>
              </button>
            ))}
          </div>
        </DialogContent>
        <DialogActions>
          <button type="button" className="px-4 py-2 text-slate-400" onClick={() => setPickerOpen(false)}>
            Close
          </button>
        </DialogActions>
      </Dialog>
      {deleteConfirmationDialog}
    </ListPageRoot>
  );
};

export default DxfLayerRulesTab;
